import { supabase } from '../lib/supabase';

class MaterialService {
  // Get all materials (no auth check)
  async getAllMaterials() {
    try {
      const { data, error } = await supabase
        ?.from('materials')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching materials:', error);
      return { data: null, error: error?.message };
    }
  }

  // Get material by ID (no auth check)
  async getMaterialById(id) {
    try {
      const { data, error } = await supabase
        ?.from('materials')
        ?.select('*')
        ?.eq('id', id)
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching material:', error);
      return { data: null, error: error?.message };
    }
  }

  // Create new material (no auth check)
  async createMaterial(materialData) {
    try {
      const { data, error } = await supabase
        ?.from('materials')
        ?.insert([{
          name: materialData?.name,
          sku: materialData?.sku,
          department: materialData?.department,
          material_type: materialData?.materialType,
          material_category: materialData?.materialCategory,
          description: materialData?.description,
          unit_cost: materialData?.unitCost,
          inventory_level: materialData?.inventoryLevel,
          reorder_level: materialData?.reorderLevel,
          unit_of_measure: materialData?.unitOfMeasure,
          supplier_name: materialData?.supplierName,
          supplier_contact: materialData?.supplierContact,
          status: materialData?.status || 'active'
        }])
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating material:', error);
      return { data: null, error: error?.message };
    }
  }

  // Update material (no auth check)
  async updateMaterial(id, materialData) {
    try {
      const updateData = {
        name: materialData?.name,
        sku: materialData?.sku,
        department: materialData?.department,
        material_type: materialData?.materialType,
        material_category: materialData?.materialCategory,
        description: materialData?.description,
        unit_cost: materialData?.unitCost,
        inventory_level: materialData?.inventoryLevel,
        reorder_level: materialData?.reorderLevel,
        unit_of_measure: materialData?.unitOfMeasure,
        supplier_name: materialData?.supplierName,
        supplier_contact: materialData?.supplierContact,
        status: materialData?.status,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase
        ?.from('materials')
        ?.update(updateData)
        ?.eq('id', id)
        ?.select();

      if (error) throw error;
      
      // Check if any rows were updated
      if (!data || data?.length === 0) {
        throw new Error('Material not found or no changes made');
      }
      
      return { data: data?.[0], error: null };
    } catch (error) {
      console.error('Error updating material:', error);
      return { data: null, error: error?.message };
    }
  }

  // Delete a material by ID (no auth check)
  async deleteMaterial(id) {
    try {
      const { error } = await supabase
        ?.from('materials')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;
      
      return { data: { id }, error: null };
    } catch (error) {
      console.error('Error deleting material:', error);
      return { data: null, error: error?.message };
    }
  }

  // Bulk delete materials (no auth check)
  async bulkDeleteMaterials(ids) {
    try {
      const { error } = await supabase
        ?.from('materials')
        ?.delete()
        ?.in('id', ids);

      if (error) throw error;
      
      return { data: { deletedIds: ids }, error: null };
    } catch (error) {
      console.error('Error bulk deleting materials:', error);
      return { data: null, error: error?.message };
    }
  }

  // Delete all materials from database (no auth check)
  async deleteAllMaterials() {
    try {
      const { error } = await supabase
        ?.from('materials')
        ?.delete()
        ?.neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all records

      if (error) throw error;
      
      return { data: { success: true }, error: null };
    } catch (error) {
      console.error('Error deleting all materials:', error);
      return { data: null, error: error?.message };
    }
  }

  // Search materials by query (no auth check)
  async searchMaterials(query) {
    try {
      const { data, error } = await supabase
        ?.from('materials')
        ?.select('*')
        ?.or(`name.ilike.%${query}%,sku.ilike.%${query}%,description.ilike.%${query}%`)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error searching materials:', error);
      return { data: null, error: error?.message };
    }
  }
}

export default new MaterialService();